// 由 src/views/smart-workbench/index.vue 的 methods 拆分而来，请勿在此文件之外重复定义同名方法。
// 基础方法：渲染辅助、报表入口、初始化、WebSocket 连接、会话切换、批量删除等
// 方法体与原文件逐字节一致（仅调整文件归属）。
import { AGENT_ID_MAP } from '@/store/modules/cosmoClaw'
import { renderMarkdownText } from '@/utils/markdownRenderer'
import { downloadPdf, downloadHtmlAsPdf } from '@/utils/pdfDownload'
import { getJobHistoryDetailApi, batchDeleteConversations } from '@/api/agentAuth'
import { WORKBENCH_COMPONENTS, getSharedWsUrl } from './workspaceConstants'

export default {
  methods: {
    renderMarkdownText,
    getAgentLabel(agentName) {
      return this.agentLabels[agentName] || agentName || ''
    },
    getBadgeLabel(session) {
      // 优先从 sessionKey 的 agentId 识别智能体
      if (session && session.key) {
        const match = session.key.match(/^agent:([^:]+):/)
        if (match && this.agentIdToLabel[match[1]]) {
          return this.agentIdToLabel[match[1]]
        }
      }
      // 回退到 session 上记录的 agentName
      return this.getAgentLabel(session && session.agentName)
    },
    handleTabClick(tab) {
      this.activeTabId = tab.id
    },
    handleTabClose(index) {
      if (index === 0) return
      const closedId = this.workbenchTabs[index].id
      this.workbenchTabs.splice(index, 1)
      if (this.activeTabId === closedId) {
        this.activeTabId = this.workbenchTabs[this.workbenchTabs.length - 1].id
      }
      if (closedId === 'center-iframe') {
        this.$store.commit('cosmoClaw/SET_CENTER_IFRAME', null)
      }
    },
    handleOpenDetail(data) {
      const item = data.item || data
      this.currentReportId = item?.id || null
      this.currentReportSource = data.source
        ? {
          source: data.source,
          tabType: data.tabType,
          index: data.index
        }
        : null
      const exists = this.workbenchTabs.find((t) => t.id === 'detail')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'detail',
          title: '报告详情',
          closable: true
        })
      }
      this.activeTabId = 'detail'
    },
    handleOpenList() {
      const exists = this.workbenchTabs.find((t) => t.id === 'list')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'list',
          title: '战情室报告',
          closable: true
        })
      }
      this.activeTabId = 'list'
    },
    handleOpenPriceManagement() {
      const exists = this.workbenchTabs.find((tab) => tab.id === 'price-management')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'price-management',
          title: '涨价管理',
          closable: true
        })
      }
      this.activeTabId = 'price-management'
    },
    handleOpenPreference() {
      const exists = this.workbenchTabs.find((tab) => tab.id === 'preference')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'preference',
          title: '决策偏好',
          closable: true
        })
      }
      this.activeTabId = 'preference'
    },
    handleOpenDetailFromList(data) {
      const item = data.item || data
      this.currentReportId = item?.id
      this.currentReportSource = data.source
        ? {
          source: data.source,
          tabType: data.tabType,
          index: data.index
        }
        : null
      const exists = this.workbenchTabs.find((t) => t.id === 'detail')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'detail',
          title: '报告详情',
          closable: true
        })
      }
      this.activeTabId = 'detail'
    },
    handleTaskOpenDetail(taskData) {
      this.currentTaskDetailId = taskData.id || taskData.taskId
      const exists = this.workbenchTabs.find((t) => t.id === 'task-detail')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'task-detail',
          title: '任务详情',
          closable: true
        })
      }
      this.activeTabId = 'task-detail'
    },
    handleTaskOpenList() {
      const exists = this.workbenchTabs.find((t) => t.id === 'task-list')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'task-list',
          title: '任务列表',
          closable: true
        })
      }
      this.activeTabId = 'task-list'
    },

    async handleViewTaskReport(task) {
      this.currentHistoryReportId = task?.id
      this.historyReportViewMode = 'doc'
      // 切换报告时重置净化缓存，避免复用上一份报告的 HTML
      this.sanitizedHistoryReportHtml = ''
      try {
        const res = await getJobHistoryDetailApi(task?.id)
        if (res && res.data) {
          this.currentHistoryReportContent = res.data.content || ''
          this.currentHistoryReportHtml = res.data.contentHtml || ''
        } else {
          this.currentHistoryReportContent = '暂无报告内容'
          this.currentHistoryReportHtml = ''
        }
      } catch (error) {
        console.error('获取历史报告详情失败:', error)
        this.currentHistoryReportContent = '获取报告失败'
        this.currentHistoryReportHtml = ''
      }
      const exists = this.workbenchTabs.find((t) => t.id === 'history-report-detail')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'history-report-detail',
          title: '历史报告详情',
          closable: true
        })
      }
      this.activeTabId = 'history-report-detail'
    },

    async handleHistoryReportDownload() {
      // 网页模式：内容渲染在 Shadow DOM 内，html2canvas 无法穿透，用离屏 iframe 承载净化后 HTML 导出
      if (this.historyReportViewMode === 'web') {
        const html = this.sanitizedHistoryReportHtml || ''
        if (!html) {
          this.$message.warning('暂无报告内容可下载')
          return
        }
        await downloadHtmlAsPdf(html, '历史报告详情.pdf')
        return
      }

      // 文档模式：截取真实 DOM（保持原有逻辑不变）
      const el = this.$refs.centerContent && this.$refs.centerContent.$refs.historyPdfContent
      if (!el) {
        this.$message.warning('暂无报告内容可下载')
        return
      }
      await downloadPdf(el, '历史报告详情.pdf')
    },

    // SafeHtmlRenderer 渲染完成后回传净化后的 HTML，缓存供下载使用
    onHistoryReportSafeHtmlRendered(sanitizedHtml) {
      this.sanitizedHistoryReportHtml = sanitizedHtml || ''
    },

    handleDecisionOpenDetail(item) {
      this.currentDecisionId = item?.id
      const exists = this.workbenchTabs.find((t) => t.id === 'decision-detail')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'decision-detail',
          title: '决策详情',
          closable: true
        })
      }
      this.activeTabId = 'decision-detail'
    },
    handleDecisionOpenList() {
      const exists = this.workbenchTabs.find((t) => t.id === 'decision-list')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'decision-list',
          title: '决策列表',
          closable: true
        })
      }
      this.activeTabId = 'decision-list'
    },
    openE2EReport() {
      // 跳转到独立 E2E 损益表路由页面（渲染 ToolFpaPlStatement demo 数据）
      // 旧版实现1（tab内嵌E2EReport.vue）:
      //   const exists = this.workbenchTabs.find((t) => t.id === 'e2e-report')
      //   if (!exists) { this.workbenchTabs.push({ id: 'e2e-report', title: 'E2E损益表', closable: true }) }
      //   this.activeTabId = 'e2e-report'
      // 旧版实现2（CFO工具面板）:
      //   this.handleOpenCfoTool({ id: 'tool-fpa-1', title: 'E2E损益表' })
      this.$router.push({ path: '/smart-engine/e2e-report' })
    },
    openCfoReportTab() {
      // 跳转到独立 CFO 专项报告路由页面（渲染 CfoReport.vue demo 数据）
      // 旧版实现（tab内嵌CfoReport.vue）:
      //   const exists = this.workbenchTabs.find((t) => t.id === 'cfo-report')
      //   if (!exists) { this.workbenchTabs.push({ id: 'cfo-report', title: 'CEO专项报告', closable: true }) }
      //   this.activeTabId = 'cfo-report'
      this.$router.push({ path: '/smart-engine/cfo-report' })
    },
    openIndustryReport() {
      const exists = this.workbenchTabs.find((t) => t.id === 'industry-operation')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'industry-operation',
          title: '五大行业经营情况',
          closable: true
        })
      }
      this.activeTabId = 'industry-operation'
    },
    openChainReport() {
      const exists = this.workbenchTabs.find((t) => t.id === 'chain-operation')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'chain-operation',
          title: '业务链群经营情况',
          closable: true
        })
      }
      this.activeTabId = 'chain-operation'
    },
    handleOpenReportHistory() {
      const exists = this.workbenchTabs.find((t) => t.id === 'report-history')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'report-history',
          title: '经营简报·全部',
          closable: true
        })
      }
      this.activeTabId = 'report-history'
    },
    handleOpenTaskQueueAll() {
      const exists = this.workbenchTabs.find((t) => t.id === 'task-queue-all')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'task-queue-all',
          title: '任务队列·全部',
          closable: true
        })
      }
      this.activeTabId = 'task-queue-all'
    },
    handleOpenDecisionsAll() {
      const exists = this.workbenchTabs.find((t) => t.id === 'decisions-all')
      if (!exists) {
        this.workbenchTabs.push({
          id: 'decisions-all',
          title: '待决策·全部',
          closable: true
        })
      }
      this.activeTabId = 'decisions-all'
    },
    handleOpenCfoTool({ id, title }) {
      if (!id) return
      const exists = this.workbenchTabs.find((t) => t.id === id)
      if (!exists) {
        this.workbenchTabs.push({ id, title: title || id, closable: true })
      }
      this.activeTabId = id
    },
    _handleBeforeUnload(e) {
      if (this.$store.state.cosmoClaw.isStreaming) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    calcHeight() {
      // 容器切换前，同步当前可见容器的 richHtml DOM 状态到 store
      const willUseDrawer = window.innerWidth < 1750
      if (willUseDrawer !== this.useDrawer) {
        this.syncRichHtmlDomToStore()
      }
      this.windowWidth = window.innerWidth
      window.isDrawerMode = this.windowWidth < 1750
      if (this.windowWidth < 1750) this.drawerVisible = false
    },
    _getAgentId(agentName) {
      return AGENT_ID_MAP[agentName] || agentName
    },
    async initializeSmartWorkbench() {
      if (!this.isSmartMode || !this.isSmartEntryRoute) return
      if (this.smartInitializationPromise) {
        return this.smartInitializationPromise
      }

      const initializationVersion = ++this.smartInitializationVersion
      const isCurrentInitialization = () =>
        initializationVersion === this.smartInitializationVersion &&
        this.isSmartMode &&
        this.isSmartEntryRoute

      const initialization = (async () => {
        await this.$store.dispatch('user/fetchWorkspaceAndAgents')
        if (!isCurrentInitialization()) return

        this.smartPlatformReady = true
        if (!this.allAgentsInitialized) {
          this.allAgentsInitialized = true
          const queryAgentId = this.$route.query.agentId
          const queryAgent = queryAgentId
            ? this.agentIdToName[queryAgentId] || queryAgentId
            : ''
          const digitalHuman = queryAgent
            ? this.digitalHumanList.find(
              (item) => item.agentName === queryAgent || item.id === queryAgent
            )
            : null
          const agent = queryAgent
            ? this.myAgentList.find(
              (item) => item.agentName === queryAgent || item.id === queryAgent
            )
            : null
          const def = this._getDefaultEntry()

          if (digitalHuman) {
            this.handleDigitalHumanClick(digitalHuman)
          } else if (agent) {
            this.handleAgentClick(agent)
          } else if (queryAgent === 'digital_plaza') {
            this.handleDigitalPlazaClick()
          } else if (queryAgent === 'agent_plaza') {
            this.handleAgentPlazaClick()
          } else if (queryAgent === 'knowledge_base') {
            this.handleKnowledgeBaseClick()
          } else if (def.type === 'digital') {
            this.handleDigitalHumanClick(def.item)
          } else if (def.type === 'agent') {
            this.handleAgentClick(def.item)
          } else {
            this.handleDigitalPlazaClick()
          }
        }

        if (isCurrentInitialization()) {
          await this._connectAllAgents()
        }
      })().catch((err) => {
        if (isCurrentInitialization()) {
          this.allAgentsInitialized = false
          this.smartPlatformReady = false
          console.warn('[SmartWorkbench] agent platform initialization failed:', err)
        }
      })

      this.smartInitializationPromise = initialization
      initialization.finally(() => {
        if (this.smartInitializationPromise === initialization) {
          this.smartInitializationPromise = null
        }
      })

      return initialization
    },
    async _connectAllAgents(retryCount = 0) {
      // A stale retry timer must not reconnect while classic workbench is active.
      if (!this.isSmartMode || !this.isSmartEntryRoute) return
      const wsUrl = getSharedWsUrl()
      if (!wsUrl) {
        const delay = Math.min(1000 * Math.pow(1.5, retryCount), 15000)
        console.log(
          `[connectAllAgents] tianzhi agent token 未就绪，${
            Math.round(delay / 100) / 10
          }s后重试...`
        )
        setTimeout(() => this._connectAllAgents(retryCount + 1), delay)
        return
      }
      // 创建单一共享 WS 连接
      await this.$store.dispatch('cosmoClaw/connectShared', {
        accessUrl: wsUrl
      })
      // 为所有智能体初始化 agentData 和 session
      const allAgentIds = Object.keys(this.$store.state.cosmoClaw.agentData)
      if (allAgentIds.length === 0) {
        // 首次初始化——确保每个 agent 有 session
        const agentNames = [
          ...this.digitalHumanList.map((a) => a.agentName),
          ...this.myAgentList.map((a) => a.agentName)
        ].filter(Boolean)
        for (const name of agentNames) {
          await this.$store.dispatch('cosmoClaw/switchAgent', name)
        }
      }
      // 切回当前 agent
      const currentAgent =
        this.$store.state.cosmoClaw.currentAgentName ||
        this.digitalHumanList[0]?.agentName ||
        this.myAgentList[0]?.agentName ||
        ''
      if (!currentAgent) return
      const currentAgentId = this._getAgentId(currentAgent)
      await this.$store.dispatch('cosmoClaw/switchAgent', currentAgent)
      const ad = this.$store.state.cosmoClaw.agentData[currentAgentId]
      if (!ad || !ad.sessions || !ad.sessions.length) {
        this.$store.dispatch('cosmoClaw/createNewSession', {
          agentName: currentAgent
        })
      }
    },
    loadCurrentWorkbenchComponent(workbench) {
      if (!this.isSmartMode || !this.isSmartEntryRoute || !this.smartPlatformReady) {
        return
      }
      const wb = workbench || this.$store.getters.currentWorkbench || {}
      let name = wb.name || ''
      // 默认显示CEO数字人工作台
      const entry = this._getDefaultEntry()
      if (entry.type === 'plaza') {
        this.$nextTick(() => this.handleDigitalPlazaClick())
        return
      }
      if (!name) {
        if (entry.type === 'digital') {
          name = (entry.item.title || 'CEO') + '工作台'
        }
      }
      if (name.includes('厂长')) {
        this.loadDynamicComponent('director')
      } else if (name.includes('CFO') || name.includes('财务')) {
        this.loadDynamicComponent('cfo')
      } else if (name.includes('CSO') || name.includes('供应链')) {
        this.loadDynamicComponent('cso')
      } else {
        this.loadDynamicComponent('ceo')
      }
    },
    loadDynamicComponent(page) {
      if (page && WORKBENCH_COMPONENTS[page]) {
        this.currentComponent = WORKBENCH_COMPONENTS[page]
      } else {
        this.currentComponent = WORKBENCH_COMPONENTS.default
      }
    },
    formatSessionTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date
      if (diff < 3600000) {
        return this.$t('workbench.minutesAgo', { n: Math.floor(diff / 60000) })
      }
      if (diff < 86400000) {
        return this.$t('workbench.hoursAgo', { n: Math.floor(diff / 3600000) })
      }
      return `${date.getMonth() + 1}-${date.getDate()}`
    },
    handleSelectAgent(agent) {
      if (!agent) return
      this.$store.dispatch('cosmoClaw/switchAgent', agent.agentName || agent.key)
      this.selectedAgent = agent.agentName || agent.key
      this.clawMode = 'assistant'
      this.agentChatStarted = false
    },
    scrollSmartChatToBottom() {
      document.dispatchEvent(new CustomEvent('cosmo-claw-scroll-bottom'))
    },
    forceScrollToBottom() {
      document.dispatchEvent(new CustomEvent('cosmo-claw-force-scroll'))
    },
    handleNewChat() {
      this.stopVoiceInput()
      this.resetLocalThinkingState()
      this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
      this.selectedAgent = null
      this.selectedDisplayType = ''
      this.selectedIframeSrc = ''
      this.clawMode = 'assistant'
      this.agentChatStarted = false
      this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
      this.$store.commit('cosmoClaw/SET_CENTER_IFRAME', null)
      this.menuIframeItem = null
      const agentName =
        this.$store.state.cosmoClaw.currentAgentName || 'chang_zhang_shu_zi_ren'
      this.$store.dispatch('cosmoClaw/createNewSession', { agentName })
    },
    openDrawer() {
      if (typeof window.openDrawer === 'function') {
        window.openDrawer()
      }
    },
    // 全选/取消全选当前 filteredSessions
    handleToggleSelectAll(val) {
      if (val) {
        var keys = this.filteredSessions.map(function (s) { return s.key })
        // 合并已选 + 全选，去重
        var set = {}
        this.selectedSessionKeys.forEach(function (k) { set[k] = true })
        keys.forEach(function (k) { set[k] = true })
        this.selectedSessionKeys = Object.keys(set)
      } else {
        // 取消当前 filteredSessions 的选择，保留其他（理论上 filteredSessions 已包含全部）
        var toRemove = {}
        this.filteredSessions.forEach(function (s) { toRemove[s.key] = true })
        this.selectedSessionKeys = this.selectedSessionKeys.filter(function (k) {
          return !toRemove[k]
        })
      }
    },
    // 单个会话 checkbox 切换（由热区点击触发）
    handleToggleSessionCheckbox(sessionKey) {
      var idx = this.selectedSessionKeys.indexOf(sessionKey)
      if (idx === -1) {
        this.selectedSessionKeys = this.selectedSessionKeys.concat([sessionKey])
      } else {
        this.selectedSessionKeys = this.selectedSessionKeys.filter(function (k) {
          return k !== sessionKey
        })
      }
    },
    handleSwitchSession(sessionKey) {
      this.stopVoiceInput()
      this.selectedSessionKeys = []
      this.mockRestoredIds = []
      const storeState = this.$store.state.cosmoClaw
      const session = storeState.sessions.find(
        (s) => s.key === sessionKey || s.globalKey === sessionKey
      )
      const agentName = (session && session.agentName) || storeState.currentAgentName
      const effectiveKey = session ? session.key : sessionKey
      if (
        storeState.currentSessionKey === effectiveKey &&
        agentName === storeState.currentAgentName
      ) {
        if (this.useDrawer) this.drawerVisible = true
        return
      }

      this.resetLocalThinkingState()
      this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
      this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
      this.$store.commit('cosmoClaw/SET_CENTER_IFRAME', null)
      this.menuIframeItem = null
      // 更新左侧高亮
      this.selectedCardId = agentName
      this._syncRouteAgent(agentName)

      const digitalHumanAgents = this.digitalHumanList
        .map((a) => a.agentName)
        .filter(Boolean)
      const isDigitalHuman = digitalHumanAgents.includes(agentName)
      if (isDigitalHuman) {
        this.selectedAgent = null
        // 保持用户的 clawMode 状态，不强制重置为 'assistant' (#状态保持)
        this.agentChatStarted = false
        // 切换到对应数字人的工作台
        const workbenchNameMap = {
          ceo_shu_zi_ren: 'CEO工作台',
          cai_wu_shu_zi_ren: 'CFO财务工作台',
          gong_ying_lian_shu_zi_ren: 'CSO供应链工作台'
        }
        const wbName = workbenchNameMap[agentName]
        if (wbName) {
          this.loadCurrentWorkbenchComponent({ name: wbName })
        }
      } else {
        this.selectedAgent = agentName
        this.clawMode = 'assistant'
        this.agentChatStarted = true
      }
      this.$store.dispatch('cosmoClaw/switchSession', {
        sessionKey: effectiveKey,
        agentName
      })
      // 同步 sessionKey 到 URL
      this._syncRouteSession(effectiveKey)
      // 抽屉模式下：数字人的会话弹出对话框，智能体不弹
      if (this.useDrawer && isDigitalHuman) {
        this.drawerVisible = true
      }
      setTimeout(
        function () {
          this._restoreMockHistoryMessages().catch(function (e) {
            console.error('[MOCK RESTORE] Error in session restore:', e)
          })
        }.bind(this),
        800
      )
    },
    handleSessionCommand(command, session) {
      if (command === 'edit') {
        this.editingSessionKey = session.key
        this.$nextTick(() => {
          const input = this.$refs.leftSidebar && this.$refs.leftSidebar.$refs.editInput && this.$refs.leftSidebar.$refs.editInput[0]
          if (input) {
            input.focus()
            input.select()
          }
        })
      } else if (command === 'delete') {
        this.$confirm('确认删除该会话？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
          .then(async () => {
            const isCurrentSession = this.currentSessionKey === session.key
            if (isCurrentSession && this.$store.state.cosmoClaw.isStreaming) {
              await this.$store.dispatch('cosmoClaw/abortConversation')
            }
            const agentName =
              session.agentName || this.$store.state.cosmoClaw.currentAgentName
            this.$store.dispatch('cosmoClaw/deleteSession', {
              sessionKey: session.key,
              agentName
            })
            if (isCurrentSession) {
              this.agentChatStarted = false
              this.$store.dispatch('cosmoClaw/createNewSession', {
                agentName: agentName || 'chang_zhang_shu_zi_ren'
              })
            }
          })
          .catch(() => {})
      }
    },
    async handleBatchDelete() {
      const count = this.selectedSessionKeys.length
      if (count === 0) return
      try {
        await this.$confirm(`确认删除选中的 ${count} 个会话？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        return
      }
      const ids = [...this.selectedSessionKeys]
      // 去掉 agent:xxx: 前缀，只传 userId|!uuid
      var convIds = ids.map(function (k) {
        var ci = k.indexOf(':', k.indexOf(':') + 1) + 1
        return ci > 0 ? k.slice(ci) : k
      })
      this.selectedSessionKeys = []
      try {
        await batchDeleteConversations(convIds)
        this.$message.success(`成功删除 ${ids.length} 个会话`)
      } catch (err) {
        console.error('批量删除会话失败:', err)
        this.$message.error('批量删除失败，请重试')
        return
      }
      // 检查删除的会话中是否包含当前会话且正在流式输出
      const isCurrentDeleted = ids.includes(this.currentSessionKey)
      if (isCurrentDeleted && this.$store.state.cosmoClaw.isStreaming) {
        await this.$store.dispatch('cosmoClaw/abortConversation')
      }
      // 逐个清理本地状态（使用现有 deleteSession action，只做本地清理）
      for (const key of ids) {
        const session = this.$store.state.cosmoClaw.sessions.find((s) => s.key === key)
        this.$store.dispatch('cosmoClaw/deleteSession', {
          sessionKey: key,
          agentName: session?.agentName
        })
      }
      // 如果当前会话被删，创建新会话
      if (isCurrentDeleted) {
        this.agentChatStarted = false
        this.$store.dispatch('cosmoClaw/createNewSession', {
          agentName:
            this.$store.state.cosmoClaw.currentAgentName || 'chang_zhang_shu_zi_ren'
        })
      }
    },
    handleMockTrigger(e) {
      var detail = e.detail || {}
      var scenario = detail.scenario
      var content = detail.content || ''
      this.persistMockMessage('user', content)
      var HANDLERS = {
        'ceo-h1-summary': function () {
          this.triggerCeoH1Summary()
        }.bind(this),
        'ceo-strategy-planning': function () {
          this.triggerCeoStrategyPlanningDemo()
        }.bind(this),
        'chain-revenue': function () {
          this.triggerChainRevenueBreakdown()
        }.bind(this),
        'chain-perf': function () {
          this.triggerChainPerformanceBreakdown()
        }.bind(this)
      }
      var handler = HANDLERS[scenario]
      if (handler) {
        handler()
      } else {
        this.$message.warning('未识别的 mock 场景：' + scenario)
      }
    },
    handleSaveLabel(sessionKey, event) {
      const newLabel = event.target.value.trim()
      this.editingSessionKey = null
      if (!newLabel) return
      const session = this.$store.state.cosmoClaw.sessions.find(
        (s) => s.key === sessionKey
      )
      if (session && session.label !== newLabel) {
        this.$store.dispatch('cosmoClaw/updateSessionLabel', {
          sessionKey,
          label: newLabel,
          agentName: session.agentName || this.$store.state.cosmoClaw.currentAgentName
        })
      }
    },
    async _restoreMockHistoryMessages() {
      var self = this
      var MAX_RETRIES = 5
      var RETRY_INTERVAL = 1500

      var doRestore = async function () {
        var messages = self.$store.state.cosmoClaw.messages
        if (!messages || messages.length === 0) return false

        var MOCK_HANDLERS = {
          'ceo-h1-summary': function (msgId) {
            return self.renderCeoH1SummaryCard(msgId)
          },
          'ceo-strategy-planning': function (msgId) {
            return self.renderCeoStrategyPlanningReportCard(msgId)
          },
          'ceo-curve1': function (msgId) {
            return self.renderCeoCurve1Report(msgId)
          },
          'ceo-curve2': function (msgId) {
            return self.renderCeoCurve2Report(msgId)
          },
          'ceo-curve3': function (msgId) {
            return self.renderCeoCurve3Report(msgId)
          },
          'ceo-industry-competitiveness': function (msgId) {
            return self.triggerIndustryCompetitiveness(msgId)
          },
          'ceo-industry-competitiveness-confirm': function (msgId) {
            return self.triggerIndustryCompetitivenessConfirm(msgId)
          },
          'chain-revenue': function (msgId) {
            return self.triggerChainRevenueBreakdown(msgId)
          },
          'chain-perf': function (msgId) {
            return self.triggerChainPerformanceBreakdown(msgId)
          },
          'cfo-margin-entry': function (msgId) {
            return self.renderCfoMarginCardWithTypewriter(msgId)
          }
        }

        var restoredCount = 0
        for (var mi = 0; mi < messages.length; mi++) {
          var msg = messages[mi]
          if (msg.type !== 'assistant') continue
          // 只恢复历史会话中的 mock marker，跳过 demo 流程实时创建的 marker（isHistory: false）
          // 避免 demo 流式动画进行中被 staticMsgId 模式覆盖
          if (msg.isHistory !== true) continue
          if (
            typeof msg.content === 'string' &&
            self.mockRestoredIds.indexOf(msg.id) === -1
          ) {
            var match = msg.content.match(/\[mock:([^\]]+)\]/)
            if (match) {
              var scenario = match[1]
              var handler = MOCK_HANDLERS[scenario]
              if (handler) {
                console.log('[MOCK RESTORE] Restoring scenario:', scenario, 'msgId:', msg.id)
                // 移除 marker 前面已有的 card HTML 消息（demo 流程中创建的重复消息），
                // 避免恢复渲染后出现两遍相同卡片
                for (var bi = mi - 1; bi >= 0; bi--) {
                  var prevMsg = messages[bi]
                  if (prevMsg.type === 'assistant' && typeof prevMsg.content === 'string' && prevMsg.content.indexOf('<div') !== -1) {
                    console.log('[MOCK RESTORE] Removing duplicate card message:', prevMsg.id)
                    self.$store.commit('cosmoClaw/REMOVE_MESSAGE_BY_ID', prevMsg.id)
                    break
                  }
                }
                self.mockRestoredIds.push(msg.id)
                restoredCount++
                try {
                  await handler(msg.id)
                } catch (e) {
                  console.error('[MOCK RESTORE] Handler error for ' + scenario + ':', e)
                }
              }
            }
          }
        }
        return restoredCount > 0
      }

      for (var retry = 0; retry <= MAX_RETRIES; retry++) {
        var restored = await doRestore()
        if (restored) break
        var currentMsgs = this.$store.state.cosmoClaw.messages
        if (currentMsgs && currentMsgs.length > 0) {
          var hasMock = currentMsgs.some(function (m) {
            return typeof m.content === 'string' && m.content.indexOf('[mock:') !== -1
          })
          if (!hasMock) break
        }
        if (retry < MAX_RETRIES) {
          await new Promise(function (resolve) {
            setTimeout(resolve, RETRY_INTERVAL)
          })
        }
      }
    }
  }
}
